import CONFIG from "@/config";
import ShowToaster from "@/toaster";
import tokenManager from "@/utils/tokenManager";
import { buildUrl } from "@/utils/url";
import { safeCaptureException } from "@/utils/sentry";
import ky from "ky";
import { ERROR_MESSAGE_SHOULD_BE_VISIBLE } from "./utils";
import ErrorCodes from "@/constants/errors/ErrorCodes";
import { isAuth0Enabled } from "@/auth/authConfig";
import { getAuthHeader, getFreshAuthHeader, requestLogin } from "@/auth/authToken";
import { setAuthStatus } from "@/auth/authState";

// Auth0 error handling for the ky interceptor. Runs only under the flag; branches
// on the backend's stable `code` (never the message). The refresh-retry relies on
// the Auth0 retry policy set in createAPIInstance ({limit:1}) — ky.retry() forces
// one retry, and beforeRequest re-attaches the freshly refreshed token.
const handleAuth0Response = async (
  request: Request,
  response: Response,
  retryCount: number,
): Promise<Response | ReturnType<typeof ky.retry> | undefined> => {
  if (!isAuth0Enabled()) return undefined;
  if (response.ok) return undefined;
  if (response.status !== 401 && response.status !== 403) return undefined;

  let code: string | undefined;
  try {
    const body = await response.clone().json();
    code = request.url.startsWith(CONFIG.endPoints.exportService)
      ? undefined
      : body?.code;
  } catch {
    // No/!JSON body — treat as an unknown 401 below.
  }

  switch (code) {
    case "AUTH-E-0006": // TOKEN_EXPIRED
    case "AUTH-E-0003": {
      // MISSING_TOKEN — refresh and retry once; a second miss falls through to login.
      if (retryCount === 0) {
        const rejected = (request.headers.get("Authorization") || "").replace(
          /^Bearer\s+/i,
          "",
        );
        const fresh = await getFreshAuthHeader(rejected || undefined);
        // No fresh token means a navigation is latched (redirecting/logging out)
        // or the state is terminal; getFreshAuthHeader already triggered login if
        // needed. Do not retry header-less.
        if (!fresh) return undefined;
        return ky.retry();
      }
      await requestLogin();
      return undefined;
    }
    case "AUTH-E-0010": // PRINCIPAL_UNRESOLVED — valid token, no account
      setAuthStatus({ phase: "blocked", reason: "PRINCIPAL_UNRESOLVED" });
      return undefined;
    case "AUTH-E-0011": // PRINCIPAL_INACTIVE — account deactivated
      setAuthStatus({ phase: "blocked", reason: "PRINCIPAL_INACTIVE" });
      return undefined;
    case "AUTH-E-0012": // TOKEN_REVOKED — force re-login
      await requestLogin();
      return undefined;
    case "AUTH-E-0002": // INVALID_TOKEN — config wrong; do not loop
    case "AUTH-E-0007": // TOKEN_AUDIENCE
      setAuthStatus({ phase: "failed", reason: "TOKEN_REJECTED", detail: code });
      safeCaptureException(new Error(`Auth token rejected: ${code}`), {
        tags: { error_type: "auth_config", auth_code: code },
      });
      return undefined;
    case "AUTH-E-0013": // SCOPE_INSUFFICIENT — no relogin, re-login cannot add a scope
    case "AUTH-E-0014": {
      // PATH_UNMAPPED — backend bug: report it
      const entry = ErrorCodes[code as keyof typeof ErrorCodes];
      if (entry) {
        ShowToaster.error(
          entry.code ? `${entry.message}\nError code: ${entry.code}` : entry.message,
        );
      }
      safeCaptureException(new Error(`Auth scope/path error: ${code}`), {
        tags: { error_type: code === "AUTH-E-0014" ? "backend_bug" : "auth_scope", auth_code: code },
      });
      return undefined;
    }
    default:
      // Any other 401 (unknown/absent code, or a refreshable one that already
      // retried) — send the user to login once. Single-flight guards the rest.
      if (response.status === 401) {
        await requestLogin();
      }
      return undefined;
  }
};

const commonHooks = {
  beforeRequest: [
    async (request: any): Promise<void> => {
      // Legacy path: unchanged, synchronous, bare token (no scheme).
      if (!isAuth0Enabled()) {
        const token = tokenManager.getToken();
        if (token) {
          request.headers.set("Authorization", token);
        }
        return;
      }
      // Auth0 path: getAuthHeader returns "Bearer <jwt>", awaiting the client
      // so early requests block rather than go out header-less.
      const header = await getAuthHeader();
      if (header) {
        request.headers.set("Authorization", header);
      }
    },
  ],

  beforeError: [
    async (error: any) => {
      const { response, request } = error;

      // Auth0 mode: 401/403 auth failures are owned by the afterResponse hook
      // above, which sets state and captures tagged telemetry. Return early so
      // the generic capture below does not double-fire (mislabeled) for them.
      if (
        isAuth0Enabled() &&
        response &&
        (response.status === 401 || response.status === 403)
      ) {
        return error;
      }

      // Legacy-only: Auth0 mode handles 401s in the afterResponse hook below.
      if (!isAuth0Enabled() && response && response.status === 401) {
        console.warn("Unauthorized request - token might be expired");
        tokenManager.removeToken();

        // redirect to login page
        const params = {
          domain: encodeURIComponent(window.location.origin),
          completeUrl: CONFIG.endPoints.auth0 + "/",
          failUrl: CONFIG.endPoints.auth0 + "/register/fail",
          serverLogin: "true",
        };
        const loginURL = buildUrl(
          CONFIG.endPoints.userService,
          "/auth/login",
          params,
        );
        window.location.href = loginURL;
      } else {
        // Capture non-401 errors to Sentry
        const apiError = new Error(
          `API Error: ${response?.status || "Network Error"} - ${request?.url || "Unknown URL"}`,
        );
        apiError.name = "APIError";

        // Get response body if available
        let responseBody: string | undefined;
        try {
          if (response) {
            const clonedResponse = response.clone();
            responseBody = await clonedResponse.text();
          }
        } catch {
          // Ignore errors reading response body
        }

        safeCaptureException(apiError, {
          tags: {
            error_type: "api_error",
            http_status: response?.status?.toString() || "unknown",
            http_method: request?.method || "unknown",
          },
          extra: {
            url: request?.url || "unknown",
            status: response?.status || "unknown",
            statusText: response?.statusText || "unknown",
            responseBody: responseBody?.substring(0, 1000) || "unavailable", // Limit response body size
            timestamp: new Date().toISOString(),
          },
        });
      }

      return error;
    },
  ],

  afterResponse: [
    // Auth0 auth handling runs FIRST, before the body-unwrapping hook below,
    // because that hook replaces the body with null and would hide the error
    // `code`. Returns a RetryMarker (forced retry), or undefined to fall through.
    async (
      request: Request,
      _options: unknown,
      response: Response,
      { retryCount }: { retryCount: number },
    ) => handleAuth0Response(request, response, retryCount),

    async (request: any, options: any, response: any) => {
      // 204/205/304 responses have no body. The Fetch spec forbids
      // constructing a Response with a non-null body (even "") when the
      // status is one of these, so touching .text()/JSON on it below throws
      // a TypeError that was being miscaptured as parse/read errors.
      if (
        response.status === 204 ||
        response.status === 205 ||
        response.status === 304
      ) {
        return response;
      }
      try {
        const parsedResponse = await response.text();
        try {
          // Auth0 mode: pass AUTH-E-* bodies through untouched so the auth hook
          // above owns them and no generic toast fires on a blocked/scope case.
          try {
            const peek = parsedResponse ? JSON.parse(parsedResponse) : null;
            if (
              isAuth0Enabled() &&
              typeof peek?.code === "string" &&
              peek.code.startsWith("AUTH-E-")
            ) {
              return new Response(parsedResponse, response);
            }
          } catch {
            // not JSON — fall through to normal handling
          }
          if (
            !parsedResponse ||
            (!parsedResponse.trim().startsWith("{") &&
              !parsedResponse.trim().startsWith("["))
          ) {
            return new Response(parsedResponse, response);
          }
          const responseJSON = JSON.parse(parsedResponse);
          if (
            !isAuth0Enabled() &&
            responseJSON?.error &&
            (responseJSON?.error === "INVALID_TOKEN" ||
              responseJSON?.error === "MISSING_TOKEN")
          ) {
            console.warn(
              "Unauthorized request - invalid token received in response body",
            );
            tokenManager.removeToken();

            // redirect to login page
            const params = {
              domain: encodeURIComponent(window.location.origin),
              completeUrl: CONFIG.endPoints.auth0 + "/",
              failUrl: CONFIG.endPoints.auth0 + "/register/fail",
              serverLogin: "true",
            };
            const loginURL = buildUrl(
              CONFIG.endPoints.userService,
              "/auth/login",
              params,
            );
            window.location.href = loginURL;
            return new Response(null, { status: 201 });
          } else if (responseJSON?.errorCode === "Q-E-0011") {
            // "No accessible questions found" — not a user-facing error; skip toast silently
            return new Response(parsedResponse, response);
          } else if (responseJSON?.error && responseJSON?.error?.length > 0) {
            // Capture API response errors to Sentry
            const apiResponseError = new Error(
              `API Response Error: ${responseJSON?.error} - ${request?.url || "Unknown URL"}`,
            );
            apiResponseError.name = "APIResponseError";

            safeCaptureException(apiResponseError, {
              tags: {
                error_type: "api_response_error",
                http_status: response?.status?.toString() || "unknown",
                http_method: request?.method || "unknown",
              },
              extra: {
                url: request?.url || "unknown",
                status: response?.status || "unknown",
                errorMessage: responseJSON?.error || "unknown",
                message: responseJSON?.message || "unknown",
                responseBody:
                  parsedResponse?.substring(0, 1000) || "unavailable",
                timestamp: new Date().toISOString(),
              },
            });

            const errorCode = responseJSON.code as string;

            // For BS-E-0006 (duplicate brand set), preserve the response body
            // so the caller can detect it and show a warning toaster instead.
            if (errorCode === "BS-E-0006") {
              return new Response(JSON.stringify(responseJSON), {
                status: responseJSON?.statusCode ?? response.status,
                headers: { "Content-Type": "application/json" },
              });
            }

            if (errorCode && ErrorCodes[errorCode as keyof typeof ErrorCodes]) {
              const error = ErrorCodes[errorCode as keyof typeof ErrorCodes];
              ShowToaster.error(
                error.code
                  ? `${error.message}\nError code: ${error.code}`
                  : error.message,
              );
            } else if (
              ERROR_MESSAGE_SHOULD_BE_VISIBLE?.includes(responseJSON?.message)
            ) {
              ShowToaster.error(
                responseJSON.code
                  ? `${responseJSON?.message}\nError code: ${responseJSON.code}`
                  : responseJSON?.message,
              );
            } else if (
              ERROR_MESSAGE_SHOULD_BE_VISIBLE?.includes(responseJSON?.error)
            ) {
              ShowToaster.error(
                responseJSON.code
                  ? `${responseJSON?.error}\nError code: ${responseJSON.code}`
                  : responseJSON?.error,
              );
            } else if (responseJSON?.statusCode === 400 || response.status === 400) {
              ShowToaster.error(
                ErrorCodes["REQ-E-0001"].code
                  ? `${ErrorCodes["REQ-E-0001"].message}\nError code: ${ErrorCodes["REQ-E-0001"].code}`
                  : ErrorCodes["REQ-E-0001"].message,
              );
            } else if (responseJSON?.statusCode === 500 || response.status === 500) {
              ShowToaster.error(
                ErrorCodes["SERVER-E-001"].code
                  ? `${ErrorCodes["SERVER-E-001"].message}\nError code: ${ErrorCodes["SERVER-E-001"].code}`
                  : ErrorCodes["SERVER-E-001"].message,
              );
            } else {
              ShowToaster.error(
                ErrorCodes["UI-E-001"].code
                  ? `${ErrorCodes["UI-E-001"].message}\nError code: ${ErrorCodes["UI-E-001"].code}`
                  : ErrorCodes["UI-E-001"].message,
              );
            }
            // Return with a non-2xx status so ky throws HTTPError and the
            // dataService catch block receives it — ensuring fetchFn gets
            // { error, errorCode } instead of { response: <error-body> }.
            return new Response(null, {
              status: responseJSON?.statusCode ?? response.status ?? 500,
              statusText: response.statusText,
              headers: response.headers,
            });
          }
          if (
            request?.url?.includes("analytics/unified") ||
            request?.url?.includes("v2/analytics/")
          ) {
            return new Response(JSON.stringify(responseJSON));
          }
          if (
            request?.url?.includes("v2/segments") ||
            request?.url?.includes("v2/segment-categories") ||
            request?.url?.includes("v2/selections") ||
            request?.url?.includes("v2/views")
          ) {
            // v2/segments follows REST semantics — pass through status/headers so
            // ky throws HTTPError on 4xx/5xx and thunks can normalise the failure.
            // v2/views follows REST semantics; ky must throw HTTPError on 4xx/5xx
            // so parseViewsV2Error can discriminate.
            return new Response(JSON.stringify(responseJSON), response);
          }
          const data = responseJSON?.data || responseJSON || {};
          return new Response(JSON.stringify(data), response);
        } catch (err: any) {
          // Capture JSON parsing errors
          const parseError = new Error(
            `Failed to parse API response: ${request?.url || "Unknown URL"}`,
          );
          parseError.name = "APIResponseParseError";
          safeCaptureException(parseError, {
            tags: {
              error_type: "api_parse_error",
              http_status: response?.status?.toString() || "unknown",
              http_method: request?.method || "unknown",
            },
            extra: {
              url: request?.url || "unknown",
              status: response?.status || "unknown",
              responsePreview:
                parsedResponse?.substring(0, 500) || "unavailable",
              timestamp: new Date().toISOString(),
            },
          });
          return new Response(parsedResponse, response);
        }
      } catch (err: any) {
        // Capture response reading errors
        const readError = new Error(
          `Failed to read API response: ${request?.url || "Unknown URL"}`,
        );
        readError.name = "APIResponseReadError";
        safeCaptureException(readError, {
          tags: {
            error_type: "api_read_error",
            http_status: response?.status?.toString() || "unknown",
            http_method: request?.method || "unknown",
          },
          extra: {
            url: request?.url || "unknown",
            status: response?.status || "unknown",
            timestamp: new Date().toISOString(),
          },
        });
      }
    },
  ],
};

// connection timeout configuration
const commonTimeout = 30000; //30 seconds

export const createAPIInstance = (baseURL: string, customOptions: any = {}) => {
  return ky.create({
    prefixUrl: baseURL,
    timeout: commonTimeout,
    hooks: commonHooks,
    // Auth0 mode: limit 1 so a forced retry from the auth afterResponse hook can
    // fire (Task 9); empty methods/statusCodes keep ordinary retries off. Legacy
    // keeps retry: 0 exactly as before.
    retry: isAuth0Enabled() ? { limit: 1, methods: [], statusCodes: [] } : 0,
    ...customOptions,
  });
};

// pre-configured API instances for services
export const apiInstances = {
  user: createAPIInstance(CONFIG.endPoints.userService),
  data: createAPIInstance(CONFIG.endPoints.dataService),
  export: createAPIInstance(CONFIG.endPoints.exportService),
};
